import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Ban, MoreVertical, Search, Filter, Download, X, Save, Edit2, Trash2, UserPlus, AlertTriangle, ShieldAlert, Clock, PlayCircle, Wallet, RefreshCw, CheckCircle, Sparkles, Award } from 'lucide-react';
import { usersAPI, authAPI, walletsAPI } from '../utils/api';
import axios from 'axios';
import constant from '../constants/constant';

const UsersManagement = () => {
  const token = sessionStorage.getItem('adminToken');
  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [users, setUsers] = useState([]);
  const [walletsMap, setWalletsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    user_type: 'USER',
    country: 'India',
    is_active: true,
    user_id: '',
    diamond_salary: ''
  });

  // Warning Modal
  const [isWarnModalOpen, setIsWarnModalOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [warnReason, setWarnReason] = useState('');

  // Tx Logs Modal
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);
  const [txUser, setTxUser] = useState(null);
  const [txLogs, setTxLogs] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  // User Level Edit Modal
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [levelUser, setLevelUser] = useState(null);
  const [userWealthLevel, setUserWealthLevel] = useState(1);
  const [userCharmLevel, setUserCharmLevel] = useState(1);
  const [levelSaving, setLevelSaving] = useState(false);

  const handleOpenLevelModal = (user) => {
    setLevelUser(user);
    setUserWealthLevel(user.wealth_level || 1);
    setUserCharmLevel(user.charm_level || 1);
    setIsLevelModalOpen(true);
  };

  const handleSaveUserLevel = async (e) => {
    e.preventDefault();
    if (!levelUser) return;
    setLevelSaving(true);
    try {
      const res = await usersAPI.updateUserLevel(levelUser.id, {
        wealth_level: userWealthLevel,
        charm_level: userCharmLevel
      });
      showToast(res.data?.message || 'User level updated and tier rewards granted successfully!');
      setIsLevelModalOpen(false);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error updating user level', 'error');
    } finally {
      setLevelSaving(false);
    }
  };

  const getTierInfo = (level) => {
    const lvl = parseInt(level) || 1;
    if (lvl <= 20) return { name: 'Bronze Tier', color: 'text-amber-700 bg-amber-50 border-amber-200', range: 'Lv 1-20' };
    if (lvl <= 40) return { name: 'Silver Tier', color: 'text-slate-700 bg-slate-100 border-slate-300', range: 'Lv 21-40' };
    if (lvl <= 60) return { name: 'Gold Tier', color: 'text-yellow-700 bg-yellow-50 border-yellow-300', range: 'Lv 41-60' };
    if (lvl <= 80) return { name: 'Platinum Tier', color: 'text-cyan-700 bg-cyan-50 border-cyan-300', range: 'Lv 61-80' };
    return { name: 'Diamond Tier', color: 'text-purple-700 bg-purple-50 border-purple-300', range: 'Lv 81-100' };
  };

  const countries = [
    { name: 'India', flag: '🇮🇳' },
    { name: 'USA', flag: '🇺🇸' },
    { name: 'UK', flag: '🇬🇧' },
    { name: 'Canada', flag: '🇨🇦' },
    { name: 'Australia', flag: '🇦🇺' },
    { name: 'UAE', flag: '🇦🇪' },
  ];

  useEffect(() => {
    fetchUsersAndWallets();
  }, []);

  const fetchUsersAndWallets = async () => {
    setLoading(true);
    try {
      const [usersRes, walletsRes] = await Promise.all([
        usersAPI.getUsers(),
        walletsAPI.getWallets()
      ]);
      
      setUsers(usersRes.data || []);
      
      const walletsData = walletsRes.data?.data || [];
      const wMap = {};
      walletsData.forEach(w => {
        wMap[w.user_id] = { balance: w.balance, diamond_balance: w.diamond_balance };
      });
      setWalletsMap(wMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching users and wallets:', err);
      setError('Failed to load user directories. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      user_type: 'USER',
      country: 'India',
      is_active: true,
      user_id: '',
      diamond_salary: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      user_type: user.user_type || 'USER',
      country: 'India',
      is_active: user.is_active ?? true,
      user_id: user.user_id || '',
      diamond_salary: user.diamond_salary || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { country, ...payload } = formData;
      // Convert diamond_salary to 0 if empty
      if (payload.diamond_salary === '' || payload.diamond_salary === null) {
        payload.diamond_salary = 0;
      }
      if (payload.user_id === '') {
        delete payload.user_id;
      }
      if (payload.phone === '') {
        delete payload.phone;
      }
      if (payload.agency_id === '') {
        payload.agency_id = null;
      }
      
      if (editingUser) {
        if (!payload.password) delete payload.password;
        await usersAPI.updateUser(editingUser.id, payload);
        showToast('User updated successfully');
      } else {
        await usersAPI.createUser(payload);
        showToast('User created successfully');
      }
      setIsModalOpen(false);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving user', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.deleteUser(id);
        showToast('User deleted successfully');
        fetchUsersAndWallets();
      } catch (err) {
        showToast('Error deleting user', 'error');
      }
    }
  };

  // BAN / UNBAN
  const handleToggleBan = async (user) => {
    const newBanState = user.is_active; // If active, we ban (ban = true). If inactive, we unban (ban = false).
    const actionText = newBanState ? 'ban' : 'unban';
    if (window.confirm(`Are you sure you want to ${actionText} ${user.first_name}?`)) {
      try {
        await axios.put(`${constant.API_BASE_URL}/admin/business/users/${user.id}/ban`, { ban: newBanState }, {
          headers: { Authorization: token }
        });
        showToast(`User ${actionText}ed successfully`);
        fetchUsersAndWallets();
      } catch (err) {
        showToast("Action failed: " + (err.response?.data?.message || err.message), 'error');
      }
    }
  };

  // WARNINGS
  const handleOpenWarnModal = (user) => {
    setTargetUser(user);
    setWarnReason('');
    setIsWarnModalOpen(true);
  };

  const handleSendWarning = async (e) => {
    e.preventDefault();
    if (!targetUser || !warnReason.trim()) return;
    try {
      await axios.post(`${constant.API_BASE_URL}/admin/business/users/${targetUser.id}/warning`, { reason: warnReason.trim() }, {
        headers: { Authorization: token }
      });
      setIsWarnModalOpen(false);
      showToast("Warning recorded successfully.");
    } catch (err) {
      showToast("Failed to record warning: " + (err.response?.data?.message || err.message), 'error');
    }
  };

  // WALLET RESET
  const handleResetWallet = async (user) => {
    if (window.confirm(`WARNING: Are you sure you want to reset ${user.first_name}'s coin wallet to 0.00? This cannot be undone.`)) {
      try {
        await axios.put(`${constant.API_BASE_URL}/admin/business/users/${user.id}/wallet/reset`, {}, {
          headers: { Authorization: token }
        });
        showToast("Wallet reset successfully");
        fetchUsersAndWallets();
      } catch (err) {
        showToast("Wallet reset failed: " + (err.response?.data?.message || err.message), 'error');
      }
    }
  };

  // TRANSACTION LOGS WINDOW
  const handleOpenTxLogs = async (user) => {
    const initialWallet = walletsMap[user.id] || {};
    setTxUser({
      ...user,
      balance: Number(initialWallet.balance || 0),
      diamond_balance: Number(initialWallet.diamond_balance || 0)
    });
    setTxLogs([]);
    setIsTxModalOpen(true);
    setTxLoading(true);

    try {
      const [walletRes, txRes] = await Promise.all([
        walletsAPI.getWallet(user.id).catch(() => null),
        walletsAPI.getTransactions(user.id).catch(() => null)
      ]);

      const wData = walletRes?.data?.data || initialWallet;
      setTxUser({
        ...user,
        balance: Number(wData.balance || 0),
        diamond_balance: Number(wData.diamond_balance || 0)
      });
      setTxLogs(txRes?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setTxLoading(false);
    }
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    if (user.user_type === 'BD') return false;
    
    if (statusFilter === 'ACTIVE' && !user.is_active) return false;
    if (statusFilter === 'BANNED' && user.is_active) return false;
    
    const term = searchTerm.toLowerCase();
    const fullName = (user.first_name + ' ' + user.last_name).toLowerCase();
    const userName = (user.user_name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const userIdStr = (user.id || '').toString().toLowerCase();
    const userCustomId = (user.user_id || '').toLowerCase();
    
    return fullName.includes(term) || 
           userName.includes(term) || 
           email.includes(term) || 
           userIdStr.includes(term) ||
           userCustomId.includes(term);
  }) : [];

  const handleRefresh = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    fetchUsersAndWallets();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">User Management</h2>
          <p className="text-slate-500 mt-1">Audit platform accounts, warning counts, ban statuses, and real-time wallet balances.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleRefresh}
            className="flex items-center gap-1.5 bg-white text-slate-700 px-4 py-2 text-sm rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition active:scale-95 cursor-pointer"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh Accounts
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-pink-600 text-white px-4 py-2 text-sm rounded-xl font-semibold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition active:scale-95 cursor-pointer"
          >
            <UserPlus size={16} /> Add New User
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">

        {/* Table Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search user accounts..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition font-semibold text-slate-600 text-sm h-10"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="BANNED">Banned</option>
            </select>
          </div>
          <div className="text-sm font-bold text-slate-500 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100">
            Total Records: <span className="text-pink-600">{filteredUsers.length}</span>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading user accounts...</p>
          </div>
        ) : error ? (
          <div className="p-20 text-center text-red-500">
            {error}
            <button onClick={fetchUsersAndWallets} className="block mx-auto mt-4 text-pink-600 font-bold underline">Try Again</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">User Profile</th>
                  <th className="p-6">Contact Info</th>
                  <th className="p-6">Wallet Balance</th>
                  <th className="p-6">Level & Tier</th>
                  <th className="p-6">User Type</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition duration-150 group">
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-bold text-lg border-2 border-white shadow-sm shrink-0">
                          {user.first_name?.[0] || 'U'}{user.last_name?.[0] || ''}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-base">{user.first_name} {user.last_name}</p>
                          <p className="text-xs font-extrabold text-pink-600">XOXO ID: {user.xoxo_id || user.user_id || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6">
                      <p className="text-slate-600 font-semibold">{user.email}</p>
                      <p className="text-xs text-slate-400 mt-1">{user.phone || 'No phone'}</p>
                    </td>
                    <td className="p-6 font-extrabold text-slate-800 text-base">
                      {walletsMap[user.id] ? walletsMap[user.id].balance?.toLocaleString() : '0'} Coins
                      {walletsMap[user.id] && walletsMap[user.id].diamond_balance > 0 && <p className="text-xs text-pink-500 font-bold mt-1">💎 {walletsMap[user.id].diamond_balance?.toLocaleString()} Diamonds</p>}
                      {user.diamond_salary && <p className="text-xs text-blue-500 font-bold mt-1">💰 {user.diamond_salary} Salary</p>}
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col gap-1.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-amber-50 text-amber-800 border border-amber-200/70 shadow-xs w-fit">
                          👑 Wealth Lv {user.wealth_level || 1}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-pink-50 text-pink-700 border border-pink-200/70 shadow-xs w-fit">
                          💖 Charm Lv {user.charm_level || 1}
                        </span>
                      </div>
                    </td>
                    <td className="p-6">
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-bold uppercase">
                        {user.user_type}
                      </span>
                    </td>
                    <td className="p-6">
                      <span className={`
                        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide
                        ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                      `}>
                        <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {user.is_active ? 'Active' : 'Banned'}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap max-w-[240px] ml-auto">
                        <button 
                          onClick={() => handleOpenLevelModal(user)}
                          className="px-2 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-md text-[10px] font-black transition flex items-center gap-1 shadow-xs cursor-pointer active:scale-95"
                          title="Edit User Level & Tier Rewards"
                        >
                          <Sparkles size={12} /> Level
                        </button>
                        <button 
                          onClick={() => handleOpenTxLogs(user)}
                          className="px-2 py-1 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                          title="View Transaction History"
                        >
                          <Wallet size={12} /> History
                        </button>
                        <button 
                          onClick={() => handleOpenWarnModal(user)}
                          className="px-2 py-1 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-md text-[10px] font-bold transition border border-yellow-100 flex items-center gap-1 cursor-pointer"
                          title="Issue System Warning"
                        >
                          <AlertTriangle size={12} /> Warn
                        </button>
                        <button 
                          onClick={() => handleToggleBan(user)}
                          className={`px-2 py-1 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer ${
                            user.is_active 
                              ? 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100' 
                              : 'bg-green-50 text-green-600 border border-green-100 hover:bg-green-100'
                          }`}
                        >
                          <Ban size={12} /> {user.is_active ? 'Ban' : 'Unban'}
                        </button>
                        <button 
                          onClick={() => handleResetWallet(user)}
                          className="px-2 py-1 bg-slate-900 text-white hover:bg-slate-800 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                          title="Reset Wallet Balance to 0"
                        >
                          <Clock size={12} /> Reset
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 hover:bg-blue-50 text-gray-400 hover:text-blue-600 rounded-md transition border border-gray-200 hover:border-blue-200 cursor-pointer" title="Edit Profile">
                          <Edit2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-gray-500">No users found matching your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Edit / Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-xl font-bold text-slate-800">{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50 transition">
                <X size={24} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              {/* Scrollable Fields Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">First Name</label>
                    <input
                      required
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">Last Name</label>
                    <input
                      required
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Email Address</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-end">
                    <label className="text-xs font-bold text-slate-500 uppercase">Password {editingUser && '(Leave blank to keep same)'}</label>
                    <span className={formData.password.length >= 6 ? "text-[10px] font-bold text-green-500" : "text-[10px] font-bold text-slate-400"}>Min 6 chars</span>
                  </div>
                  <div className="relative">
                    <input
                      required={!editingUser}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Phone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Diamond Salary</label>
                  <input
                    type="number"
                    name="diamond_salary"
                    value={formData.diamond_salary}
                    onChange={handleInputChange}
                    placeholder="e.g. 5000"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-semibold"
                  >
                    {countries.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.flag} {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">User Role</label>
                  <select
                    required
                    name="user_type"
                    value={formData.user_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-semibold"
                  >
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                    <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                    <option value="MANAGER">MANAGER</option>
                    <option value="HOST">HOST</option>
                  </select>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded text-pink-600 focus:ring-pink-500 border-slate-300 cursor-pointer"
                  />
                  <label htmlFor="is_active" className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
                    User is Active
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-8 py-2.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 shadow-lg shadow-slate-200 transition active:scale-95"
                  >
                    <Save size={18} /> {editingUser ? 'Save Changes' : 'Create User'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Warning Message Modal */}
      {isWarnModalOpen && targetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <AlertTriangle className="text-yellow-500" size={20} /> Issue Warning
              </h3>
              <button onClick={() => setIsWarnModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSendWarning} className="p-6 space-y-4">
              <p className="text-xs text-slate-500 font-semibold">
                Warning details will be recorded as a Security Alert log linked to {targetUser.first_name}'s account.
              </p>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-400 uppercase">Warning Reason</label>
                <textarea
                  required
                  rows={3}
                  placeholder="e.g. Terms violation or suspicious login pattern observed."
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition text-sm font-medium"
                  value={warnReason}
                  onChange={(e) => setWarnReason(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsWarnModalOpen(false)}
                  className="px-5 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl text-xs font-bold active:scale-95 transition"
                >
                  Issue Alert
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Logs Modal (Coins & Diamonds Statement) */}
      {isTxModalOpen && txUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0 bg-gradient-to-r from-slate-900 to-indigo-950 text-white">
              <div className="flex items-center gap-3">
                <img 
                  src={txUser.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(txUser.first_name || 'User')}&background=fce7f3&color=db2777`}
                  alt="Avatar"
                  className="w-12 h-12 rounded-full object-cover border-2 border-pink-500"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black">{txUser.first_name} {txUser.last_name || ''}</h3>
                    <span className="px-2.5 py-0.5 bg-pink-500 text-white font-extrabold text-[11px] rounded-full tracking-wider">
                      XOXO ID: {txUser.xoxo_id || txUser.user_id || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">{txUser.email}</p>
                </div>
              </div>

              {/* Balance Badges */}
              <div className="flex items-center gap-2.5">
                <div className="bg-white/10 px-3.5 py-1.5 rounded-xl border border-white/15 text-right">
                  <p className="text-[9px] uppercase font-bold text-amber-300">Coins Balance</p>
                  <p className="text-sm font-black text-white">🪙 {Number(txUser.balance || 0).toLocaleString()}</p>
                </div>
                <div className="bg-white/10 px-3.5 py-1.5 rounded-xl border border-white/15 text-right">
                  <p className="text-[9px] uppercase font-bold text-pink-300">Diamonds Balance</p>
                  <p className="text-sm font-black text-pink-300">💎 {Number(txUser.diamond_balance || 0).toLocaleString()}</p>
                </div>
                <button onClick={() => setIsTxModalOpen(false)} className="text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition ml-2">
                  <X size={22} />
                </button>
              </div>
            </div>
            
            {/* Modal Body: Statement Table */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 space-y-3">
              {txLoading ? (
                <div className="p-20 text-center">
                  <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className="text-xs font-bold text-slate-500">Loading complete transaction ledger...</p>
                </div>
              ) : txLogs.length === 0 ? (
                <div className="p-20 text-center text-slate-400">
                  <Wallet size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="font-bold text-slate-600">No activity recorded for this user.</p>
                  <p className="text-xs mt-1">This user has no past recharges, gifts, rewards, or deductions.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center px-1 mb-1">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-400">
                      Audit Statement ({txLogs.length} Records)
                    </p>
                    <span className="text-[11px] text-slate-500 font-semibold">Real-time ledger entries</span>
                  </div>

                  {txLogs.map((tx) => {
                    const txType = (tx.transaction_type || '').toUpperCase();
                    const isDiamond = (tx.currency_type || '').toUpperCase() === 'DIAMOND';
                    const isCredit = txType === 'COIN_ADD' || 
                                    txType === 'SELF_RECHARGE' || 
                                    txType === 'FRIEND_RECHARGE' || 
                                    txType === 'ROOM_BONUS_CLAIM' || 
                                    txType === 'TARGET_REWARD' ||
                                    (tx.balance_after !== undefined && tx.balance_before !== undefined && Number(tx.balance_after) > Number(tx.balance_before));
                    
                    return (
                      <div key={tx.id} className="p-4 bg-white rounded-2xl border border-slate-200/80 flex items-center justify-between shadow-sm hover:shadow-md transition">
                        <div className="flex items-center gap-3.5">
                          <div className={`p-2.5 rounded-xl ${
                            isCredit ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                          }`}>
                            {isCredit ? <Clock size={16} /> : <Ban size={16} />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide border ${
                                isDiamond ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}>
                                {isDiamond ? '💎 DIAMOND' : '🪙 COIN'}
                              </span>
                              <span className="text-xs font-black text-slate-800 uppercase">{tx.transaction_type}</span>
                              <span className="text-[10px] text-slate-400 font-mono">TXN-{String(tx.id).substring(0, 8)}</span>
                            </div>
                            <p className="text-xs font-bold text-slate-700 mt-1">{tx.reason || 'Transaction executed'}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{new Date(tx.created_at || tx.timestamp).toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className={`text-base font-black ${isCredit ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {isCredit ? '+' : '-'}{Math.abs(Number(tx.amount || 0)).toLocaleString()} {isDiamond ? '💎' : '🪙'}
                          </p>
                          <div className="text-[10px] font-semibold text-slate-500 mt-0.5 bg-slate-100 px-2 py-0.5 rounded-md">
                            {isDiamond ? '💎' : '🪙'} {Number(tx.balance_before || 0).toLocaleString()} → {Number(tx.balance_after || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
              <button 
                onClick={() => setIsTxModalOpen(false)}
                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition shadow-sm"
              >
                Close Statement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 👑 EDIT USER LEVEL & TIER REWARDS MODAL */}
      {isLevelModalOpen && levelUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[28px] w-full max-w-xl overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="p-6 bg-gradient-to-r from-purple-700 via-indigo-700 to-pink-600 text-white flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-md">
                  <Sparkles size={24} className="text-amber-300 animate-pulse" />
                </div>
                <div>
                  <h3 className="font-extrabold text-xl">Edit User Level & Tier Rewards</h3>
                  <p className="text-purple-200 text-xs font-medium mt-0.5">
                    Adjust user level (1 - 100). Tier rewards unlock automatically!
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsLevelModalOpen(false)}
                className="p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveUserLevel} className="p-6 space-y-6 overflow-y-auto">
              
              {/* User Identity Card */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between">
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-black text-base border-2 border-white shadow-sm">
                    {levelUser.first_name?.[0] || 'U'}{levelUser.last_name?.[0] || ''}
                  </div>
                  <div>
                    <h4 className="font-black text-slate-800 text-base">{levelUser.first_name} {levelUser.last_name}</h4>
                    <p className="text-xs font-black text-purple-600 font-mono">XOXO ID: {levelUser.xoxo_id || levelUser.user_id}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="px-3 py-1 bg-slate-200/80 text-slate-700 rounded-full text-xs font-extrabold uppercase">
                    {levelUser.user_type || 'USER'}
                  </span>
                </div>
              </div>

              {/* 👑 Wealth Level Selector */}
              <div className="p-5 bg-gradient-to-br from-amber-50/50 to-orange-50/30 rounded-2xl border border-amber-200/80 space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">👑</span>
                    <label className="font-black text-slate-800 text-sm">Wealth Level</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border ${getTierInfo(userWealthLevel).color}`}>
                      {getTierInfo(userWealthLevel).name} ({getTierInfo(userWealthLevel).range})
                    </span>
                    <span className="text-base font-black text-amber-700 bg-amber-100 px-3 py-0.5 rounded-xl">
                      Lv {userWealthLevel}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={userWealthLevel}
                    onChange={(e) => setUserWealthLevel(parseInt(e.target.value) || 1)}
                    className="w-full accent-amber-600 h-2 bg-slate-200 rounded-lg cursor-pointer"
                  />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-bold">Quick Presets:</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {[1, 5, 10, 20, 40, 60, 80, 100].map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => setUserWealthLevel(lvl)}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold transition ${
                            userWealthLevel === lvl 
                              ? 'bg-amber-600 text-white shadow-xs' 
                              : 'bg-white text-slate-600 hover:bg-amber-100 border border-slate-200'
                          }`}
                        >
                          Lv {lvl}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 💖 Charm Level Selector */}
              <div className="p-5 bg-gradient-to-br from-pink-50/50 to-rose-50/30 rounded-2xl border border-pink-200/80 space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💖</span>
                    <label className="font-black text-slate-800 text-sm">Charm Level</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border ${getTierInfo(userCharmLevel).color}`}>
                      {getTierInfo(userCharmLevel).name} ({getTierInfo(userCharmLevel).range})
                    </span>
                    <span className="text-base font-black text-pink-700 bg-pink-100 px-3 py-0.5 rounded-xl">
                      Lv {userCharmLevel}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={userCharmLevel}
                    onChange={(e) => setUserCharmLevel(parseInt(e.target.value) || 1)}
                    className="w-full accent-pink-600 h-2 bg-slate-200 rounded-lg cursor-pointer"
                  />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-bold">Quick Presets:</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {[1, 5, 10, 20, 40, 60, 80, 100].map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => setUserCharmLevel(lvl)}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold transition ${
                            userCharmLevel === lvl 
                              ? 'bg-pink-600 text-white shadow-xs' 
                              : 'bg-white text-slate-600 hover:bg-pink-100 border border-slate-200'
                          }`}
                        >
                          Lv {lvl}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Automatic Backpack Rewards Alert */}
              <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200 flex items-start gap-3">
                <Award size={20} className="text-purple-600 shrink-0 mt-0.5" />
                <div className="text-xs text-purple-900 leading-relaxed font-semibold">
                  <p className="font-extrabold text-purple-950 mb-0.5">Automatic Level Tier Backpack Rewards</p>
                  Saving new levels (e.g. 1 to 10 or 10 to 1) immediately unlocks the matching Avatar Frame & Chat Bubble into the user's backpack inventory and sends an in-app push notification to the user.
                </div>
              </div>

              {/* Modal Footer */}
              <div className="pt-2 flex justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsLevelModalOpen(false)}
                  className="px-5 py-2.5 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={levelSaving}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-xs font-extrabold transition flex items-center gap-2 shadow-md hover:shadow-lg active:scale-95 disabled:opacity-50 cursor-pointer"
                >
                  {levelSaving ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" /> Saving & Granting Rewards...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} /> Save Level & Unlock Rewards
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default UsersManagement;